import React, { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { StepperComponent } from "../../../../_start/assets/ts/components";
import { KTSVG } from "../../../../_start/helpers";
import { IBrandingSetupData, defaultBrandingSetupData, TBrandTone, IColorPalette } from "./BrandingSetupModel";
import { db } from "../../../../config/firebase";
import { doc, collection, updateDoc } from "firebase/firestore";
import { useBrandingSuggestion, ColorPalette, BrandVoiceSuggestion } from "../../../../hooks/useBrandingSuggestion";

interface Props {
  userID: string;
  projectTitle: string;
  projectID: string;
  projectDescription?: string;
  projectMotivation?: string;
}

const brandTones: { id: TBrandTone; label: string; icon: string; color: string; description: string }[] = [
  {
    id: "Inspirational",
    label: "Inspirational",
    icon: "bi-rocket-takeoff",
    color: "success",
    description: "Motivational language to encourage action. Example: Nike's \"Just Do It\"",
  },
  {
    id: "Informal",
    label: "Informal & Humorous",
    icon: "bi-emoji-laughing",
    color: "danger",
    description: "Engage audiences with playful tone. Example: Duolingo's quirky approach",
  },
  {
    id: "Innovative",
    label: "Innovative",
    icon: "bi-lightbulb",
    color: "warning",
    description: "Emphasize cutting-edge ideas. Example: Tesla's mission-driven voice",
  },
  {
    id: "Conversational",
    label: "Conversational",
    icon: "bi-chat-heart",
    color: "info",
    description: "Feels like talking to a friend. Example: Slack's human voice",
  },
  {
    id: "Bold",
    label: "Bold & Authentic",
    icon: "bi-megaphone",
    color: "primary",
    description: "Speak directly and genuinely. Example: Fenty Beauty's bold voice",
  },
];

const BrandingSetupPage: React.FC<Props> = ({
  userID,
  projectTitle,
  projectID,
  projectDescription = "",
}) => {
  const navigate = useNavigate();
  const stepperRef = useRef<HTMLDivElement | null>(null);
  const stepper = useRef<StepperComponent | null>(null);
  const [data, setData] = useState<IBrandingSetupData>(defaultBrandingSetupData);
  const [selectedPaletteIndex, setSelectedPaletteIndex] = useState<number>(0);
  const [hasLoadedSuggestions, setHasLoadedSuggestions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const {
    isLoading: isLoadingPalettes,
    error: paletteError,
    palettes,
    recommendedIndex,
    brandVoice,
    suggestPalette,
  } = useBrandingSuggestion();

  const loadStepper = () => {
    stepper.current = StepperComponent.createInsance(
      stepperRef.current as HTMLDivElement
    );
  };

  const updateData = useCallback((fieldsToUpdate: Partial<IBrandingSetupData>) => {
    setData((prevData) => ({ ...prevData, ...fieldsToUpdate }));
  }, []);

  // Load AI suggestions on mount
  useEffect(() => {
    if (projectDescription && projectID && !hasLoadedSuggestions) {
      suggestPalette(projectID, projectDescription, projectTitle);
      setHasLoadedSuggestions(true);
    }
  }, [projectDescription, projectID, projectTitle, suggestPalette, hasLoadedSuggestions]);

  // Update selected palette when recommendations load
  useEffect(() => {
    if (palettes.length > 0) {
      setSelectedPaletteIndex(recommendedIndex);
      const palette = palettes[recommendedIndex];
      if (palette) {
        updateData({
          brandColor: palette.primary,
          colorPalette: palette as IColorPalette,
        });
      }
    }
  }, [palettes, recommendedIndex, updateData]);

  // Update brand voice when AI suggestion loads
  useEffect(() => {
    if (brandVoice?.recommended) {
      const validTones = brandTones.map(t => t.id);
      if (validTones.includes(brandVoice.recommended as TBrandTone)) {
        updateData({ brandTone: brandVoice.recommended as TBrandTone });
      }
    }
  }, [brandVoice, updateData]);

  const handlePaletteSelect = (index: number) => {
    setSelectedPaletteIndex(index);
    const palette = palettes[index];
    if (palette) {
      updateData({
        brandColor: palette.primary,
        colorPalette: palette as IColorPalette,
      });
    }
  };

  const prevStep = () => {
    if (!stepper.current) return;
    stepper.current.goPrev();
  };

  const nextStep = () => {
    if (!stepper.current) return;
    stepper.current.goNext();
  };

  const writeData = async () => {
    const userRef = doc(db, "users", userID);
    const projectsRef = collection(userRef, "projects");
    const projectDoc = doc(projectsRef, projectID);
    const projectData = {
      brandColor: data.brandColor,
      brandLogo: data.brandLogo,
      brandTone: data.brandTone,
      colorPalette: data.colorPalette || null,
    };
    await updateDoc(projectDoc, projectData);
  };

  const submit = async () => {
    setIsSaving(true);
    try {
      await writeData();
      // Navigate back to project dashboard
      navigate("/project", {
        state: {
          projectTitle,
          projectID,
          projectDescription,
          userID,
        },
      });
    } catch (error) {
      console.error("Failed to save branding:", error);
      setIsSaving(false);
    }
  };

  useEffect(() => {
    if (!stepperRef.current) return;
    loadStepper();
  }, []);

  const renderPaletteCard = (palette: ColorPalette, index: number) => {
    const isSelected = selectedPaletteIndex === index;
    const isRecommended = index === recommendedIndex;

    return (
      <div
        key={index}
        className={`card cursor-pointer mb-4 ${
          isSelected ? "border border-primary border-2" : "border"
        }`}
        onClick={() => handlePaletteSelect(index)}
        style={{ transition: "all 0.2s ease" }}
      >
        <div className="card-body p-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <input
                type="radio"
                className="form-check-input me-3"
                checked={isSelected}
                onChange={() => handlePaletteSelect(index)}
              />
              <span className="fw-bold fs-5">{palette.name}</span>
            </div>
            {isRecommended && (
              <span className="badge badge-light-primary">
                <i className="bi bi-stars me-1"></i>Recommended
              </span>
            )}
          </div>

          {/* Color swatches */}
          <div className="d-flex gap-2 mb-3">
            <div
              className="rounded"
              style={{
                width: "48px",
                height: "48px",
                backgroundColor: palette.primary,
                border: "1px solid #e0e0e0",
              }}
              title={`Primary: ${palette.primary}`}
            />
            <div
              className="rounded"
              style={{
                width: "48px",
                height: "48px",
                backgroundColor: palette.secondary,
                border: "1px solid #e0e0e0",
              }}
              title={`Secondary: ${palette.secondary}`}
            />
            <div
              className="rounded"
              style={{
                width: "48px",
                height: "48px",
                backgroundColor: palette.accent,
                border: "1px solid #e0e0e0",
              }}
              title={`Accent: ${palette.accent}`}
            />
            <div
              className="rounded"
              style={{
                width: "48px",
                height: "48px",
                backgroundColor: palette.background,
                border: "1px solid #e0e0e0",
              }}
              title={`Background: ${palette.background}`}
            />
            <div
              className="rounded"
              style={{
                width: "48px",
                height: "48px",
                backgroundColor: palette.text,
                border: "1px solid #e0e0e0",
              }}
              title={`Text: ${palette.text}`}
            />
          </div>

          {/* Color labels */}
          <div className="d-flex gap-2 mb-3">
            <span className="fs-8 text-muted" style={{ width: "48px", textAlign: "center" }}>Primary</span>
            <span className="fs-8 text-muted" style={{ width: "48px", textAlign: "center" }}>Secondary</span>
            <span className="fs-8 text-muted" style={{ width: "48px", textAlign: "center" }}>Accent</span>
            <span className="fs-8 text-muted" style={{ width: "48px", textAlign: "center" }}>Background</span>
            <span className="fs-8 text-muted" style={{ width: "48px", textAlign: "center" }}>Text</span>
          </div>

          {palette.explanation && (
            <p className="text-muted fs-7 mb-0 fst-italic">
              <i className="bi bi-info-circle me-1"></i>
              {palette.explanation}
            </p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="card card-custom">
      <div className="card-body p-10 p-lg-15 p-xxl-30">
        <div
          ref={stepperRef}
          className="stepper stepper-1 d-flex flex-column flex-xl-row flex-row-fluid"
          id="kt_modal_create_app_stepper"
        >
          {/* Stepper Nav */}
          <div className="d-flex justify-content-center justify-content-xl-start flex-row-auto w-100 w-xl-300px">
            <div className="stepper-nav d-flex flex-column pt-5">
              <div className="stepper-item current" data-kt-stepper-element="nav">
                <div className="stepper-wrapper">
                  <div className="stepper-icon">
                    <i className="stepper-check fas fa-check"></i>
                    <span className="stepper-number">1</span>
                  </div>
                  <div className="stepper-label">
                    <h3 className="stepper-title">Color Palette</h3>
                    <div className="stepper-desc">Select brand colors</div>
                  </div>
                </div>
              </div>

              <div className="stepper-item" data-kt-stepper-element="nav">
                <div className="stepper-wrapper">
                  <div className="stepper-icon">
                    <i className="stepper-check fas fa-check"></i>
                    <span className="stepper-number">2</span>
                  </div>
                  <div className="stepper-label">
                    <h3 className="stepper-title">Logo</h3>
                    <div className="stepper-desc">Upload or generate a logo</div>
                  </div>
                </div>
              </div>

              <div className="stepper-item" data-kt-stepper-element="nav">
                <div className="stepper-wrapper">
                  <div className="stepper-icon">
                    <i className="stepper-check fas fa-check"></i>
                    <span className="stepper-number">3</span>
                  </div>
                  <div className="stepper-label">
                    <h3 className="stepper-title">Brand Voice</h3>
                    <div className="stepper-desc">Choose tone of voice</div>
                  </div>
                </div>
              </div>

              <div className="stepper-item" data-kt-stepper-element="nav">
                <div className="stepper-wrapper">
                  <div className="stepper-icon">
                    <i className="stepper-check fas fa-check"></i>
                    <span className="stepper-number">4</span>
                  </div>
                  <div className="stepper-label">
                    <h3 className="stepper-title">Complete</h3>
                    <div className="stepper-desc">Review and save</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="d-flex flex-row-fluid justify-content-center">
            <form
              className="pb-5 w-100 w-md-400px w-xl-600px"
              noValidate
              id="kt_modal_create_app_form"
            >
              {/* Step 1: Color Palette */}
              <div className="pb-5 current" data-kt-stepper-element="content">
                <div className="w-100">
                  <div className="pb-5 pb-lg-10">
                    <h3 className="fw-bolder text-dark display-6">Color Palette</h3>
                    <p className="text-muted fs-6">
                      Based on your business description, we've suggested color palettes that match your brand personality.
                    </p>
                  </div>

                  {paletteError && (
                    <div className="alert alert-danger mb-5">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {paletteError}
                    </div>
                  )}

                  {isLoadingPalettes ? (
                    <div className="text-center py-10">
                      <div className="spinner-border text-primary mb-4"></div>
                      <p className="text-muted">Analyzing your business to suggest color palettes...</p>
                    </div>
                  ) : palettes.length > 0 ? (
                    <>
                      <div className="mb-5">
                        {palettes.map((palette, index) => renderPaletteCard(palette, index))}
                      </div>

                      <button
                        type="button"
                        className="btn btn-sm btn-light-primary"
                        onClick={() => suggestPalette(projectID, projectDescription, projectTitle)}
                        disabled={isLoadingPalettes}
                      >
                        <i className="bi bi-arrow-clockwise me-1"></i>
                        Regenerate Suggestions
                      </button>
                    </>
                  ) : (
                    <div className="text-center py-10">
                      <i className="bi bi-palette fs-3x text-muted mb-4 d-block"></i>
                      <p className="text-muted mb-4">
                        {projectDescription
                          ? "Click below to generate color palette suggestions"
                          : "Add a project description to get AI-powered color suggestions"}
                      </p>
                      {projectDescription && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={() => suggestPalette(projectID, projectDescription, projectTitle)}
                          disabled={isLoadingPalettes}
                        >
                          <i className="bi bi-magic me-2"></i>
                          Generate Suggestions
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Step 2: Logo */}
              <div className="pb-5" data-kt-stepper-element="content">
                <div className="w-100">
                  <div className="pb-10 pb-lg-15">
                    <h3 className="fw-bolder text-dark display-6">Logo</h3>
                    <p className="text-muted fs-6">
                      Upload your existing logo or use AI to generate one.
                    </p>
                  </div>

                  <div className="fv-row mb-5">
                    <label className="fs-6 fw-bolder text-dark mb-3">Upload Logo</label>
                    <input
                      type="file"
                      className="form-control form-control-lg form-control-solid"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            updateData({ brandLogo: reader.result as string });
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    <span className="text-muted fs-7 mt-2 d-block">
                      PNG, JPG or SVG. Max 2MB.
                    </span>
                  </div>

                  {data.brandLogo && (
                    <div className="mb-5">
                      <label className="fs-6 fw-bolder text-dark mb-3">Preview</label>
                      <div className="p-5 bg-light rounded text-center">
                        <img
                          src={data.brandLogo}
                          alt="Logo preview"
                          style={{ maxWidth: "200px", maxHeight: "200px" }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="separator separator-dashed my-6"></div>

                  <p className="text-muted fs-7">
                    <i className="bi bi-info-circle me-1"></i>
                    You can also generate a logo using AI from the Project Dashboard.
                  </p>
                </div>
              </div>

              {/* Step 3: Brand Voice */}
              <div className="pb-5" data-kt-stepper-element="content">
                <div className="w-100">
                  <div className="pb-10 pb-lg-15">
                    <h3 className="fw-bolder text-dark display-6">Brand Voice</h3>
                    <p className="text-muted fs-6">
                      How do you want your brand to sound? This influences all your messaging.
                    </p>
                  </div>

                  {/* AI Recommendation Banner */}
                  {brandVoice?.explanation && (
                    <div className="alert alert-primary d-flex align-items-center mb-5">
                      <i className="bi bi-stars fs-3 me-3"></i>
                      <div>
                        <span className="fw-bold">AI Recommendation: </span>
                        <span>{brandVoice.explanation}</span>
                      </div>
                    </div>
                  )}

                  <div className="fv-row">
                    {brandTones.map((tone) => {
                      const isRecommended = brandVoice?.recommended === tone.id;
                      return (
                        <label
                          key={tone.id}
                          className={`d-flex align-items-center justify-content-between cursor-pointer mb-4 p-4 rounded border ${
                            data.brandTone === tone.id
                              ? "border-primary bg-light-primary"
                              : "border-gray-300 bg-hover-light"
                          }`}
                          style={{ transition: "all 0.2s ease" }}
                        >
                          <span className="d-flex align-items-center me-2">
                            <span className="symbol symbol-50px me-4">
                              <span className={`symbol-label bg-light-${tone.color}`}>
                                <i className={`bi ${tone.icon} text-${tone.color} fs-2x`}></i>
                              </span>
                            </span>
                            <span className="d-flex flex-column">
                              <span className="d-flex align-items-center">
                                <span className="fw-bolder fs-6">{tone.label}</span>
                                {isRecommended && (
                                  <span className="badge badge-light-primary ms-2">
                                    <i className="bi bi-stars me-1"></i>Recommended
                                  </span>
                                )}
                              </span>
                              <span className="fs-7 text-muted">{tone.description}</span>
                            </span>
                          </span>
                          <span className="form-check form-check-custom form-check-solid">
                            <input
                              className="form-check-input"
                              type="radio"
                              name="brandTone"
                              value={tone.id}
                              checked={data.brandTone === tone.id}
                              onChange={() => updateData({ brandTone: tone.id })}
                            />
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Step 4: Complete */}
              <div className="pb-5" data-kt-stepper-element="content">
                <div className="w-100">
                  <div className="pb-10 pb-lg-15">
                    <h3 className="fw-bolder text-dark display-6">Review & Save</h3>
                    <p className="text-muted fs-6">Review your branding setup before saving.</p>
                  </div>

                  {/* Color Palette Summary */}
                  <div className="mb-8">
                    <h4 className="fw-bolder mb-3">
                      <i className="bi bi-palette me-2 text-primary"></i>
                      Color Palette
                    </h4>
                    {data.colorPalette ? (
                      <div className="bg-light rounded p-4">
                        <div className="fw-bold mb-2">{data.colorPalette.name}</div>
                        <div className="d-flex gap-2">
                          {["primary", "secondary", "accent", "background", "text"].map((key) => (
                            <div key={key} className="text-center">
                              <div
                                className="rounded mb-1"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  backgroundColor: data.colorPalette?.[key as keyof IColorPalette] as string,
                                  border: "1px solid #e0e0e0",
                                }}
                              />
                              <span className="fs-9 text-muted text-capitalize">{key}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="text-muted">No palette selected</div>
                    )}
                  </div>

                  {/* Logo Summary */}
                  <div className="mb-8">
                    <h4 className="fw-bolder mb-3">
                      <i className="bi bi-image me-2 text-primary"></i>
                      Logo
                    </h4>
                    {data.brandLogo ? (
                      <div className="bg-light rounded p-4 text-center">
                        <img
                          src={data.brandLogo}
                          alt="Logo"
                          style={{ maxWidth: "100px", maxHeight: "100px" }}
                        />
                      </div>
                    ) : (
                      <div className="text-muted">No logo uploaded</div>
                    )}
                  </div>

                  {/* Brand Voice Summary */}
                  <div className="mb-8">
                    <h4 className="fw-bolder mb-3">
                      <i className="bi bi-megaphone me-2 text-primary"></i>
                      Brand Voice
                    </h4>
                    <div className="bg-light rounded p-4">
                      <span className="fw-bold">{data.brandTone}</span>
                      <span className="text-muted ms-2">
                        {brandTones.find((t) => t.id === data.brandTone)?.description}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="d-flex justify-content-between pt-10">
                <div className="mr-2">
                  <button
                    type="button"
                    className="btn btn-lg btn-light-primary fw-bolder py-4 pe-8 me-3"
                    data-kt-stepper-action="previous"
                    onClick={prevStep}
                  >
                    <KTSVG
                      path="/media/icons/duotone/Navigation/Left-2.svg"
                      className="svg-icon-3 me-1"
                    />
                    Previous
                  </button>
                </div>
                <div>
                  <button
                    type="button"
                    className="btn btn-lg btn-primary fw-bolder py-4 ps-8 me-3"
                    data-kt-stepper-action="submit"
                    onClick={submit}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        Save Branding
                        <KTSVG
                          path="/media/icons/duotone/Navigation/Right-2.svg"
                          className="svg-icon-3 ms-2"
                        />
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    className="btn btn-lg btn-primary fw-bolder py-4 ps-8 me-3"
                    data-kt-stepper-action="next"
                    onClick={nextStep}
                  >
                    Next Step
                    <KTSVG
                      path="/media/icons/duotone/Navigation/Right-2.svg"
                      className="svg-icon-3 ms-1"
                    />
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export { BrandingSetupPage };
